import { useEffect, useRef, useState, type ReactNode } from "react";
import { species } from "@redchain/gen1-data";
import type { TradeRow, BattleRow } from "../api/cableClub";
import { useGen1Nav } from "./useGen1Nav";

type Listing = TradeRow | BattleRow;
type Command = {
  label: string;
  name?: string;
  run: () => void;
  disabled?: boolean;
};
type Position = {
  ids: string[] | null;
  captured?: Listing[];
  page: number;
  selected?: string;
};
const emptyPosition = (): Position => ({ ids: null, page: 0 });
const name = (id: number) => species(id)?.name ?? `#${id}`;
const range = (min = 1, max = 100) =>
  min === 1 && max === 100
    ? "Any Lv"
    : min === max
      ? `Lv${min}`
      : `Lv${min}-${max}`;

/** Keep positions in the parent so opening a listing does not reset the browser. */
export function useBoardBrowser(
  own: boolean,
  listings: Listing[],
  mine: Listing[],
) {
  const [positions, setPositions] = useState({
    browse: emptyPosition(),
    mine: emptyPosition(),
  });
  const key = own ? "mine" : "browse";
  const position = positions[key];
  const rows = own ? mine : listings.filter((r) => !r.mine);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [filter, setFilter] = useState({
    species: "",
    min: 1,
    max: 100,
    matches: false,
  });
  const [draft, setDraft] = useState({
    species: "",
    min: "1",
    max: "100",
    matches: false,
  });
  const update = (change: Partial<Position>) =>
    setPositions((p) => ({ ...p, [key]: { ...p[key], ...change } }));
  useEffect(() => {
    if (position.ids === null)
      update({ ids: rows.map((r) => r.id), captured: rows });
  }, [key, position.ids, rows.map((r) => r.id).join(",")]);
  const ids = own
    ? rows.map((r) => r.id)
    : (position.ids ?? rows.map((r) => r.id));
  const byId = new Map(rows.map((r) => [r.id, r]));
  const known = new Set(ids);
  const newCount = rows.filter((r) => !known.has(r.id)).length;
  const activeFilters =
    !own &&
    (!!filter.species ||
      filter.min !== 1 ||
      filter.max !== 100 ||
      filter.matches);
  const visible = ids.filter((id) => {
    const row = position.captured?.find((r) => r.id === id) ?? byId.get(id);
    if (!row) return !activeFilters; // A removed post keeps its slot until an explicit refresh.
    if (own) return true;
    if ("species" in row)
      return (
        name(row.species)
          .toLowerCase()
          .includes(filter.species.toLowerCase()) &&
        row.level >= filter.min &&
        row.level <= filter.max &&
        (!filter.matches || row.qualifyingSlots.length > 0)
      );
    return row.levels.every((l) => l >= filter.min && l <= filter.max);
  });
  const pages = Math.max(1, Math.ceil(visible.length / 3));
  const page = Math.min(position.page, pages - 1);
  const pageRows = visible
    .slice(page * 3, page * 3 + 3)
    .map((id) => ({ id, row: byId.get(id) }));
  return {
    filtersOpen,
    draft,
    setDraft,
    activeFilters,
    openFilters: () => {
      setDraft({
        species: filter.species,
        min: String(filter.min),
        max: String(filter.max),
        matches: filter.matches,
      });
      setFiltersOpen(true);
    },
    closeFilters: () => setFiltersOpen(false),
    applyFilters: () => {
      const min = Number(draft.min),
        max = Number(draft.max);
      if (
        !Number.isInteger(min) ||
        !Number.isInteger(max) ||
        min < 1 ||
        max > 100 ||
        min > max
      )
        throw new Error(
          "Levels must be 1-100, with minimum no higher than maximum.",
        );
      setFilter({ ...draft, min, max });
      update({ page: 0, selected: undefined });
      setFiltersOpen(false);
    },
    clearFilters: () => {
      setFilter({ species: "", min: 1, max: 100, matches: false });
      update({ page: 0, selected: undefined });
      setFiltersOpen(false);
    },
    refresh: () => update({ ids: null }),
    select: (id: string) => update({ selected: id }),
    pageRows,
    page,
    pages,
    newCount,
    selected: position.selected,
    move: (delta: number) =>
      update({
        page: Math.max(0, Math.min(pages - 1, page + delta)),
        selected: undefined,
      }),
  };
}

export function BoardBrowser({
  label,
  own,
  count,
  model,
  post,
  openMine,
  openBrowse,
  nearby,
  refresh,
  select,
  close,
  notice,
  locked,
}: {
  label: string;
  own: boolean;
  count: number;
  model: ReturnType<typeof useBoardBrowser>;
  post?: () => void;
  openMine: () => void;
  openBrowse: () => void;
  nearby?: () => void;
  refresh: () => void;
  select: (row: Listing) => void;
  close: () => void;
  notice?: string;
  locked?: boolean;
}) {
  const top: Command[] = [
    { label: "Browse", run: openBrowse },
    { label: "My posts", name: `My posts (${count})`, run: openMine },
  ];
  if (post)
    top.push({
      label: "Post",
      name: `Post ${label.toLowerCase()}`,
      run: post,
      disabled: locked,
    });
  const tools: Command[] = [];
  if (!own)
    tools.push({
      label: model.activeFilters ? "Filters*" : "Filters",
      run: model.openFilters,
    });
  tools.push({
    label: "Trainers",
    name: "Nearby trainers",
    run: nearby ?? (() => {}),
    disabled: !nearby || locked,
  });
  tools.push({
    label: model.newCount ? `New (${model.newCount > 99 ? "99+" : model.newCount})` : "Refresh",
    name: model.newCount
      ? `New listings available (${model.newCount})`
      : "Refresh",
    run: refresh,
  });
  const entries: Command[] = model.pageRows.map(({ id, row }) => ({
    label: row?.trainer ?? "Post unavailable",
    run: () => {
      if (row && !locked) {
        model.select(id);
        select(row);
      }
    },
  }));
  const pager: Command[] = [
    {
      label: "Prev",
      name: "Previous page",
      run: () => model.move(-1),
      disabled: model.page === 0,
    },
    {
      label: "Next",
      name: "Next page",
      run: () => model.move(1),
      disabled: model.page + 1 >= model.pages,
    },
  ];
  const all = [
    ...top,
    ...tools,
    ...entries,
    ...pager,
    { label: "Close", run: close },
  ];
  const nav = useGen1Nav(
    all.length,
    (i) => {
      if (!all[i]?.disabled) all[i]?.run();
    },
    close,
  );
  const restored = useRef(false);
  useEffect(() => {
    const index = model.pageRows.findIndex((r) => r.id === model.selected);
    if (!restored.current && index >= 0)
      nav.setIndex(top.length + tools.length + index);
    restored.current = true;
  }, []);
  const buttons = (items: Command[], offset: number) => (
    <ul className="club-browser__commands" role="listbox">
      {items.map((r, i) => (
        <li
          key={r.name ?? r.label}
          role="option"
          aria-label={r.name ?? r.label}
          aria-disabled={r.disabled || undefined}
          aria-selected={nav.index === offset + i}
          onClick={() => {
            nav.setIndex(offset + i);
            if (!r.disabled) r.run();
          }}
        >
          <span aria-hidden="true">
            {nav.index === offset + i ? "▶" : "\u00a0"}
          </span>
          {r.label}
        </li>
      ))}
    </ul>
  );
  return (
    <div
      className="wallet-menu"
      role="dialog"
      aria-modal="true"
      aria-label={`${label} board`}
    >
      <div
        className="wallet-menu__lcd wallet-menu__lcd--full"
        data-screen="cable-club"
      >
        <div className="wallet-menu__box">
          <div className="club-browser">
            <h2 className="cable-club-board__heading">
              {own ? "My posts" : `${label} board`}
              {notice && (
                <span
                  className="club-browser__notice"
                  title={notice}
                  role="status"
                >
                  {notice}
                </span>
              )}
            </h2>
            {buttons(top, 0)}
            {buttons(tools, top.length)}
            <div className="cable-club-board__scroll club-browser__results">
              {!model.pageRows.length && (
                <p>
                  {model.activeFilters
                    ? "No matching posts"
                    : own
                      ? "No active posts"
                      : `No ${label.toLowerCase()}s posted`}
                </p>
              )}
              <ul role="listbox" className="club-browser__list">
                {model.pageRows.map(({ id, row }, i) => (
                  <li
                    key={id}
                    role="option"
                    data-listing-id={id}
                    aria-disabled={!row || locked}
                    aria-label={
                      row
                        ? `${row.trainer} ${"species" in row ? `${name(row.species)} Lv${row.level}` : `${row.partySize} Pokémon Lv${Math.min(...row.levels)}-${Math.max(...row.levels)}`}`
                        : "Post unavailable"
                    }
                    aria-selected={nav.index === top.length + tools.length + i}
                    onClick={() => {
                      nav.setIndex(top.length + tools.length + i);
                      entries[i]?.run();
                    }}
                  >
                    <span className="club-browser__cursor" aria-hidden="true">
                      {nav.index === top.length + tools.length + i
                        ? "▶"
                        : "\u00a0"}
                    </span>
                    <div>
                      {row ? (
                        "species" in row ? (
                          <>
                            <Line
                              left={name(row.species)}
                              right={`Lv${row.level}`}
                            />
                            <Line
                              left={`Want ${row.wants === "any" ? "Any" : name(row.wants)}`}
                              right={range(row.minLevel, row.maxLevel)}
                            />
                          </>
                        ) : (
                          <>
                            <Line
                              left={row.trainer}
                              right={`${row.partySize} Pokémon`}
                            />
                            <Line
                              left="Party levels"
                              right={range(
                                Math.min(...row.levels),
                                Math.max(...row.levels),
                              )}
                            />
                          </>
                        )
                      ) : (
                        <>
                          <span>Post unavailable</span>
                          <small>Refresh for new posts</small>
                        </>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            </div>
            <div className="club-browser__footer">
              <div className="club-browser__pager">
                <span>
                  Page {model.page + 1} of {model.pages}
                </span>
                {buttons(pager, top.length + tools.length + entries.length)}
              </div>
              {buttons([{ label: "Close", run: close }], all.length - 1)}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
function Line({ left, right }: { left: ReactNode; right: ReactNode }) {
  return (
    <span className="club-browser__line">
      <span>{left}</span>
      <span>{right}</span>
    </span>
  );
}
